import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ADMIN_AUTH_KEY, checkAdmin } from '../services/api';

/**
 * Single-admin auth state. The admin logs in with a username/password; the
 * base64 token is stored in localStorage and attached to every request by the
 * axios interceptor (see services/api.js). Narrators never log in — `isAdmin`
 * stays false and the recording flow works anonymously.
 */
const AuthContext = createContext({ isAdmin: false, login: async () => {}, logout: () => {} });

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);

  // On mount, if a token is already stored, confirm it still authenticates
  // (the admin password may have been rotated on the server).
  useEffect(() => {
    if (!localStorage.getItem(ADMIN_AUTH_KEY)) return;
    let cancelled = false;
    checkAdmin()
      .then(({ is_admin }) => {
        if (cancelled) return;
        if (is_admin) {
          setIsAdmin(true);
        } else {
          localStorage.removeItem(ADMIN_AUTH_KEY);
        }
      })
      .catch(() => {
        localStorage.removeItem(ADMIN_AUTH_KEY);
      });
    return () => { cancelled = true; };
  }, []);

  // A 401 on any request that carried the token clears it and fires this event.
  useEffect(() => {
    const onLogout = () => setIsAdmin(false);
    window.addEventListener('pp-admin-logout', onLogout);
    return () => window.removeEventListener('pp-admin-logout', onLogout);
  }, []);

  const login = useCallback(async (username, password) => {
    const token = btoa(`${username}:${password}`);
    localStorage.setItem(ADMIN_AUTH_KEY, token);
    try {
      const { is_admin } = await checkAdmin();
      if (!is_admin) {
        localStorage.removeItem(ADMIN_AUTH_KEY);
        throw new Error('Invalid admin credentials');
      }
      setIsAdmin(true);
    } catch (err) {
      localStorage.removeItem(ADMIN_AUTH_KEY);
      setIsAdmin(false);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    setIsAdmin(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAdmin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
